/** Shared notification toasts — used by Judge and Producer pages. */

import { useState, useCallback } from "react";
import { playDing } from "@/utils/ding";

const JUDGE_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#a855f7",
  "#06b6d4", "#ec4899", "#14b8a6", "#f97316", "#6366f1",
];

export interface Notification {
  id: number;
  text: string;
  color?: string;
  /** If set, rendered as a colored judge badge */
  judgeName?: string;
  judgeNum?: number;
  /** Small detail text after main text */
  detail?: string;
}

export function useNotifications(maxItems = 15, dismissMs = 4000) {
  const [items, setItems] = useState<Notification[]>([]);

  const notify = useCallback(
    (n: Omit<Notification, "id"> & { ding?: boolean }) => {
      const id = Date.now() + Math.random();
      setItems((prev) => [{ id, ...n }, ...prev].slice(0, maxItems));
      setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== id)), dismissMs);
      if (n.ding) playDing();
    },
    [maxItems, dismissMs],
  );

  const clear = useCallback(() => setItems([]), []);

  return { items, notify, clear };
}

export function NotificationToasts({ items }: { items: Notification[] }) {
  if (!items.length) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 60,
        right: 16,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        zIndex: 900,
        pointerEvents: "none",
      }}
    >
      {items.map((n) => {
        const accentColor = n.judgeNum
          ? JUDGE_COLORS[(n.judgeNum - 1) % JUDGE_COLORS.length]
          : n.color ?? "var(--c-border)";
        return (
          <div
            key={n.id}
            style={{
              background: "var(--c-surface)",
              border: `1px solid ${accentColor}`,
              borderLeft: `3px solid ${accentColor}`,
              padding: "8px 14px",
              fontSize: 12,
              color: "var(--c-text)",
              maxWidth: 340,
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              animation: "fadeIn 0.15s ease-out",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {n.judgeName && (
              <span
                style={{
                  color: accentColor,
                  fontWeight: 700,
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                {n.judgeName}
              </span>
            )}
            <span>{n.text}</span>
            {n.detail && (
              <span
                style={{
                  background: "var(--c-surface-2, #1e1e1e)",
                  border: "1px solid var(--c-border)",
                  borderRadius: 3,
                  padding: "1px 6px",
                  fontSize: 11,
                  fontFamily: "var(--font-mono, monospace)",
                  fontWeight: 700,
                  color: accentColor,
                  flexShrink: 0,
                }}
              >
                {n.detail}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
